/**
 * B5: campañas de envío financiado. Lista `shippingCampaigns` y un formulario sencillo; el servidor
 * (admin-updateShippingCampaign) valida con ShippingCampaignSchema y escribe la auditoría. Importes en
 * EUR y en céntimos en Firestore; aquí se editan en euros.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, money, muted, Notice, str, Table, td, when } from '../lib/ui.js';

type Doc = Record<string, unknown>;

interface Form {
  id: string;
  name: string;
  active: boolean;
  countries: string;
  category_ids: string;
  buyer_price_eur: string;
  budget_eur: string;
  starts_at: string;
  ends_at: string;
  reason: string;
}

const localInput = (d: Date): string => {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const toDate = (v: unknown): Date =>
  v && typeof v === 'object' && 'toDate' in v ? (v as { toDate: () => Date }).toDate() : new Date();

const blank = (): Form => ({
  id: '',
  name: '',
  active: true,
  countries: '',
  category_ids: '',
  buyer_price_eur: '0.99',
  budget_eur: '200',
  starts_at: localInput(new Date()),
  ends_at: localInput(new Date(Date.now() + 7 * 86_400_000)),
  reason: '',
});

const fromDoc = (c: Doc): Form => ({
  id: str(c.id),
  name: str(c.name),
  active: c.active === true,
  countries: ((c.countries as string[] | undefined) ?? []).join(', '),
  category_ids: ((c.category_ids as string[] | undefined) ?? []).join(', '),
  buyer_price_eur: String(
    (((c.buyer_price as Doc | undefined)?.amount as number | undefined) ?? 0) / 100,
  ),
  budget_eur: String((((c.budget as Doc | undefined)?.amount as number | undefined) ?? 0) / 100),
  starts_at: localInput(toDate(c.starts_at)),
  ends_at: localInput(toDate(c.ends_at)),
  reason: '',
});

const list = (s: string): string[] =>
  s
    .split(/[,\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);
const cents = (s: string): number => Math.round(Number(s.replace(',', '.')) * 100);

export function CampaignsPage(): ReactElement {
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = (): void => {
    getDocs(query(collection(db, 'shippingCampaigns'), orderBy('created_at', 'desc')))
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch(() => {
        setRows([]);
      });
  };
  useEffect(load, []);

  const set = (k: keyof Form, v: string | boolean): void => {
    setForm((f) => (f ? { ...f, [k]: v } : f));
  };
  const save = (): void => {
    if (!form) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    const data = {
      name: form.name.trim(),
      active: form.active,
      countries: list(form.countries).map((c) => c.toUpperCase()),
      category_ids: list(form.category_ids),
      buyer_price: { amount: cents(form.buyer_price_eur), currency: 'EUR' },
      budget: { amount: cents(form.budget_eur), currency: 'EUR' },
      starts_at: new Date(form.starts_at).getTime(),
      ends_at: new Date(form.ends_at).getTime(),
    };
    call(api.admin.updateShippingCampaign, {
      ...(form.id ? { id: form.id } : {}),
      data,
      ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
    })
      .then((r) => {
        setNotice(`Campaña guardada (${r.id}).`);
        setForm(null);
        load();
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <>
      <h1 style={{ margin: 0 }}>Campañas de envío</h1>
      <p style={muted}>
        «Envío a 0,99 € hasta el domingo»: el comprador paga como máximo el tope y nuvvu asume la
        diferencia hasta agotar el presupuesto (entonces se desactiva sola). Países = destino; vacío
        = todos. Categorías = ids de categoría (o cualquiera de su ruta); vacío = todas.
      </p>
      <Notice text={notice} />
      <Notice text={error} error />
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table
          head={[
            'Nombre',
            'Activa',
            'Países',
            'Categorías',
            'Tope',
            'Presupuesto',
            'Gastado',
            'Pedidos',
            'Desde',
            'Hasta',
            '',
          ]}
        >
          {rows.map((c) => (
            <tr key={str(c.id)}>
              <td style={td}>{str(c.name)}</td>
              <td style={td}>{c.active === true ? 'sí' : 'no'}</td>
              <td style={td}>{((c.countries as string[] | undefined) ?? []).join(', ') || '*'}</td>
              <td style={td}>
                {((c.category_ids as string[] | undefined) ?? []).join(', ') || '*'}
              </td>
              <td style={td}>{money(c.buyer_price)}</td>
              <td style={td}>{money(c.budget)}</td>
              <td style={td}>{money(c.spent)}</td>
              <td style={td}>{str(c.orders_count)}</td>
              <td style={td}>{when(c.starts_at)}</td>
              <td style={td}>{when(c.ends_at)}</td>
              <td style={td}>
                <button
                  type="button"
                  style={btn(false)}
                  onClick={() => {
                    setForm(fromDoc(c));
                  }}
                >
                  Editar
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td style={td} colSpan={11}>
                Sin campañas.
              </td>
            </tr>
          )}
        </Table>
      )}
      {!form && (
        <button
          type="button"
          style={{ ...btn(true), justifySelf: 'start' }}
          onClick={() => {
            setForm(blank());
          }}
        >
          Nueva campaña
        </button>
      )}
      {form && (
        <form
          style={{ ...card, display: 'grid', gap: 10, maxWidth: 560 }}
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <strong>{form.id ? `Editar ${form.id}` : 'Nueva campaña'}</strong>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Nombre
            <input
              style={field}
              value={form.name}
              required
              onChange={(e) => {
                set('name', e.target.value);
              }}
            />
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => {
                set('active', e.target.checked);
              }}
            />
            Activa
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Países de destino (ISO-2, separados por comas; vacío = todos)
            <input
              style={field}
              value={form.countries}
              placeholder="ES, PT, HU"
              onChange={(e) => {
                set('countries', e.target.value);
              }}
            />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Ids de categoría (separados por comas; vacío = todas)
            <input
              style={field}
              value={form.category_ids}
              placeholder="women_shoes, kids"
              onChange={(e) => {
                set('category_ids', e.target.value);
              }}
            />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Tope para el comprador (€; 0 = gratis)
              <input
                style={field}
                inputMode="decimal"
                value={form.buyer_price_eur}
                onChange={(e) => {
                  set('buyer_price_eur', e.target.value);
                }}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Presupuesto total (€)
              <input
                style={field}
                inputMode="decimal"
                value={form.budget_eur}
                onChange={(e) => {
                  set('budget_eur', e.target.value);
                }}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Desde
              <input
                style={field}
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => {
                  set('starts_at', e.target.value);
                }}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Hasta
              <input
                style={field}
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => {
                  set('ends_at', e.target.value);
                }}
              />
            </label>
          </div>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Motivo (auditoría)
            <input
              style={field}
              value={form.reason}
              onChange={(e) => {
                set('reason', e.target.value);
              }}
            />
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" style={btn(true)} disabled={busy}>
              Guardar
            </button>
            <button
              type="button"
              style={btn(false)}
              onClick={() => {
                setForm(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
    </>
  );
}
